import { Component, computed, inject, input, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { ICONS } from '@core/services/icons/icons.service';
import { isNil } from '@core/utils/shared-utils';

import { TableRowError } from '@shared/components/table/table.model';

/**
 * The errors naming one and the same set of rows, ascending; empty for a global error.
 * A finding about several rows is one group, not one per row.
 */
interface ErrorGroup<T> {
  rowNumbers: number[];
  errors: TableRowError<T>[];
}

/** Row numbers listed in full before the label switches to naming a remainder. */
const MAX_LISTED_ROWS = 6;

@Component({
  selector: 'app-errors-list',
  imports: [TranslatePipe],
  templateUrl: './errors-list.component.html'
})
export class ErrorsListComponent<T> {
  errors = input.required<TableRowError<T>[]>();

  private readonly translate = inject(TranslateService);

  protected readonly ICONS = ICONS;

  protected readonly collapsed = signal(false);

  protected readonly rowCount = computed<number>(
    () =>
      new Set(
        this.errors()
          .filter((error) => error.rowNumber != null)
          .map((error) => error.rowNumber)
      ).size
  );

  protected readonly groups = computed<ErrorGroup<T>[]>(() => {
    const byRowSet = new Map<string, ErrorGroup<T>>();
    for (const error of this.errors()) {
      const rowNumbers = this.rowsNamedBy(error);
      const group = byRowSet.get(rowNumbers.join(','));
      if (isNil(group)) {
        byRowSet.set(rowNumbers.join(','), { rowNumbers, errors: [error] });
        continue;
      }
      // The entries of a multi-row finding are one per row and render the same pill, so
      // only the first is kept: the copies would repeat the same sentence with nothing to
      // tell them apart.
      const isAlreadyShown = group.errors.some(
        (shown) => shown.message === error.message && shown.column === error.column
      );
      if (!isAlreadyShown) {
        group.errors.push(error);
      }
    }

    return [...byRowSet.values()].sort(
      (a, b) => this.firstRow(a.rowNumbers) - this.firstRow(b.rowNumbers)
    );
  });

  /** A global error sorts after every row group rather than among them. */
  private firstRow(rowNumbers: number[]): number {
    return rowNumbers[0] ?? Number.MAX_SAFE_INTEGER;
  }

  private rowsNamedBy(error: TableRowError<T>): number[] {
    if (error.rowNumbers?.length) {
      return [...error.rowNumbers].sort((a, b) => a - b);
    }
    return isNil(error.rowNumber) ? [] : [error.rowNumber];
  }

  /**
   * The label for a group naming several rows. Long lists are cut off because the label sits
   * on one line beside the messages; the rows it does not name are still highlighted in the
   * table, which is where the reader goes to act on them.
   */
  protected rowsLabel(rowNumbers: number[]): string {
    const listed = rowNumbers.slice(0, MAX_LISTED_ROWS);
    const rows = listed.join(', ');
    const remaining = rowNumbers.length - listed.length;
    return remaining > 0
      ? this.translate.instant('table.error.lines_more', { rows, count: remaining })
      : this.translate.instant('table.error.lines', { rows });
  }

  protected toggleCollapsed(): void {
    this.collapsed.update((collapsed) => !collapsed);
  }

  protected trackGroup(index: number, group: ErrorGroup<T>): string {
    return `${group.rowNumbers.join(',') || 'global'}_${index}`;
  }

  protected trackErrorByMessage(index: number, error: TableRowError<T>): string {
    return `${error.message}_${error.rowNumber}_${index}`;
  }
}
